import { IconTrash } from 'assets/img';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LwButton, LwFormTime } from 'redesign';
import { useEffect, useMemo } from 'react';
import { Grid, styled } from '@mui/material';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { isInvalidSegmentRange, isOverlappingSegment } from '../utils/is-overlapping-segment';

type TimeslotProps = {
  index: number;
  handleDelete: (index: number) => void;
  additionalAvailabilities?: SegmentsForEdit[];
};
export const Timeslot = ({ index, handleDelete, additionalAvailabilities }: TimeslotProps) => {
  const { t } = useTranslation('all-platforms-common');
  const { control, watch, setError, clearErrors } = useFormContext<TimeSlotFormContext>();
  const timeslots = watch('availability');
  const segmentKind = watch('segmentKind');
  const workerSelect = watch('workerSelect');

  //I don't like this at all, but there's something extremely off about how array values play with the native validation
  const validateTimeSlot = (slot: SegmentsForEdit) => {
    const overlappingSlot = isOverlappingSegment(slot, [
      ...timeslots.filter((_, i) => i !== index),
      ...(additionalAvailabilities ?? []),
    ]);
    const isInternallyInvalid = isInvalidSegmentRange(slot.utcStartDateTime, slot.utcEndDateTime);

    const startField = `availability.${index}.utcStartDateTime` as const;
    const endField = `availability.${index}.utcEndDateTime` as const;
    const overlappingErrorMessage = t('edit_availability.timeslot_overlap', {
      ns: 'all-platforms-common',
    });
    const internallyInvalidErrorMessage = t('edit_availability.timeslot_invalid', {
      ns: 'all-platforms-common',
    });
    if (isInternallyInvalid) {
      const error = { type: 'manual', message: internallyInvalidErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else if (overlappingSlot) {
      const error = { type: 'manual', message: overlappingErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else {
      clearErrors([startField, endField]);
    }
  };

  const isDeleteButtonDisabled = useMemo(() => {
    return timeslots.length <= 1;
  }, [timeslots.length]);

  useEffect(() => {
    validateTimeSlot(timeslots[index]);
    //This dependency needs to be stringified like this because the logic contained within validateTimeSlot
    //relies on the properties of every member of the timeslots array
    //forgive me
  }, [
    JSON.stringify(timeslots),
    JSON.stringify(additionalAvailabilities),
    segmentKind,
    workerSelect,
  ]);

  return (
    <StyledContainer container xs={24}>
      <Grid container spacing={2} xs={12}>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcStartDateTime`}
            label={t('edit_availability.start_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcEndDateTime`}
            label={t('edit_availability.end_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} mt={2} justifyContent={'flex-end'}>
        <DeleteButton
          startIcon={<IconTrash width={18} height={18} />}
          disabled={isDeleteButtonDisabled}
          color="tertiary"
          onClick={() => handleDelete(index)}
        >
          {t('edit_availability.delete_timeslot', { ns: 'all-platforms-common' })}
        </DeleteButton>
      </Grid>
    </StyledContainer>
  );
};

const StyledContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const DeleteButton = styled(LwButton)(({ theme }) => ({
  '&.MuiButton-root': {
    '& svg path': {
      stroke: theme.palette.lwDanger[100],
      fill: 'none',
    },
  },
  '&.MuiButton-root.MuiButton-containedTertiary svg path': {
    stroke: theme.palette.lwDanger[100],
    fill: 'none',
  },
  '&.MuiButton-containedTertiary': {
    border: `1px solid ${theme.palette.lwDanger[100]}`,
    color: theme.palette.lwDanger[100],
    '&.Mui-disabled': {
      color: theme.palette.lwDanger[100],
    },
    '&:hover': {
      border: `1px solid ${theme.palette.lwDanger[100]}`,
      boxShadow: `0px 6px 16px ${theme.palette.lwDanger[20]}`,
      textDecoration: 'none',
    },
  },
}));
